import { Badge, Button, Tooltip } from 'antd';
import type React from 'react';
import type {
  ExpressNewOrdersMultiQueryQuery,
  NewReadyAndPickedOrdersMultiQueryQuery,
} from '@/generated/graphql/graphql';

type OrderType =
  | NewReadyAndPickedOrdersMultiQueryQuery['order'][number]
  | ExpressNewOrdersMultiQueryQuery['order'][number];

type DepartmentMap = Map<number, { name: string; count: number }>;
type DepartmentStatusMap = Map<number, { name: string; statuses: Map<string, number> }>;

export function groupOrdersByDepartment<T extends OrderType>(
  orders: T[] | undefined | null
): DepartmentMap {
  if (!orders) return new Map<number, { name: string; count: number }>();

  return orders.reduce((acc, order) => {
    const deptId = order.departmentId;
    const deptName = order.department?.name || 'Unknown';
    const existing = acc.get(deptId);

    if (existing) {
      existing.count++;
    } else {
      acc.set(deptId, { name: deptName, count: 1 });
    }

    return acc;
  }, new Map<number, { name: string; count: number }>());
}

export function groupOrdersByDepartmentAndStatus<T extends OrderType>(
  orders: T[] | undefined | null
): DepartmentStatusMap {
  if (!orders) return new Map<number, { name: string; statuses: Map<string, number> }>();

  return orders.reduce((acc, order) => {
    const deptId = order.departmentId;
    const deptName = order.department?.name || 'Unknown';
    const statusName = order.statusName || 'Unknown';
    const existing = acc.get(deptId);

    if (existing) {
      const statusCount = existing.statuses.get(statusName) || 0;
      existing.statuses.set(statusName, statusCount + 1);
    } else {
      const statusMap = new Map<string, number>();
      statusMap.set(statusName, 1);
      acc.set(deptId, { name: deptName, statuses: statusMap });
    }

    return acc;
  }, new Map<number, { name: string; statuses: Map<string, number> }>());
}

export function createDepartmentTooltip(
  departmentMap: DepartmentMap,
  onDepartmentClick: (departmentId: number) => void
): React.ReactNode {
  if (departmentMap.size === 0) return null;

  return (
    <div style={{ padding: '4px 0' }}>
      {Array.from(departmentMap.entries()).map(([deptId, { name, count }]) => (
        <Button
          key={deptId}
          type="link"
          onClick={() => onDepartmentClick(deptId)}
          style={{
            padding: '4px 8px',
            height: 'auto',
            display: 'block',
          }}
        >
          {name}: {count}
        </Button>
      ))}
    </div>
  );
}

export function createDepartmentStatusTooltip(
  departmentStatusMap: DepartmentStatusMap,
  onDepartmentClick: (departmentId: number) => void
): React.ReactNode {
  if (departmentStatusMap.size === 0) return null;

  return (
    <div style={{ padding: '4px 0' }}>
      {Array.from(departmentStatusMap.entries()).map(([deptId, { name, statuses }]) => {
        const totalCount = Array.from(statuses.values()).reduce((sum, count) => sum + count, 0);
        const statusOrder = ['New', 'DriverAssigned'];
        const statusText = Array.from(statuses.entries())
          .sort(([statusA], [statusB]) => {
            const indexA = statusOrder.indexOf(statusA);
            const indexB = statusOrder.indexOf(statusB);
            const orderA = indexA === -1 ? 999 : indexA;
            const orderB = indexB === -1 ? 999 : indexB;
            return orderA - orderB;
          })
          .map(([status, count]) => `${status}: ${count}`)
          .join(', ');

        return (
          <Button
            key={deptId}
            type="link"
            onClick={() => onDepartmentClick(deptId)}
            style={{
              padding: '4px 8px',
              height: 'auto',
              display: 'block',
              textAlign: 'left',
              whiteSpace: 'normal',
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {name}: {totalCount}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>{statusText}</div>
          </Button>
        );
      })}
    </div>
  );
}

interface OrdersBadgeProps {
  label: string;
  count: number;
  color: string;
  tooltip: React.ReactNode;
}

export const OrdersBadge: React.FC<OrdersBadgeProps> = ({ label, count, color, tooltip }) => {
  return (
    <Tooltip title={tooltip} styles={{ container: { padding: '8px' } }}>
      <Badge
        style={{ marginRight: 16, cursor: 'pointer' }}
        count={`${label}: ${count}`}
        showZero={false}
        color={color}
      />
    </Tooltip>
  );
};
